import type { SiteSettings, FaqItem } from "@/types";

// 자주 묻는 질문 기본값. DB(faqItems)가 비어 있으면 이 값이 쓰인다.
export const DEFAULT_FAQ: FaqItem[] = [
  {
    question: "배송은 얼마나 걸리나요?",
    answer:
      "우체국택배를 이용하며, 주문 확인 후 2~3일(영업일 기준) 내 발송됩니다. 수확 시기에 따라 일부 지연될 수 있습니다.",
  },
  {
    question: "배송비는 얼마인가요?",
    answer:
      "기본 배송비가 부과되며, 일정 금액 이상 구매 시 무료배송입니다. 정확한 배송비와 무료배송 기준은 상품 페이지의 배송 안내를 참고해 주세요.",
  },
  {
    question: "교환·환불이 가능한가요?",
    answer:
      "신선 농산물은 관련 법령(전자상거래법)에 따라 단순 변심에 의한 교환·반품이 제한됩니다. 다만 상품에 하자가 있거나 파손·변질되었거나 주문과 다른 상품이 배송된 경우에는, 수령 직후(부패·변질은 즉시) 상품 사진과 함께 고객센터로 연락 주시면 신속히 교환 또는 환불해 드립니다.",
  },
  {
    question: "예약 주문은 어떻게 진행되나요?",
    answer:
      "예약 주문 상품은 수확 시기에 맞춰 발송됩니다. 예약 주문 완료 후 수확이 완료되면 개별 안내 후 발송됩니다.",
  },
  {
    question: "무통장입금은 언제까지 해야 하나요?",
    answer:
      "주문 후 24시간 이내에 입금해주세요. 미입금 시 주문이 자동 취소됩니다.",
  },
  {
    question: "도매·대량 구매가 가능한가요?",
    answer:
      "10박스 이상 대량 구매는 전화 또는 이메일로 문의해주시면 별도 협의드립니다.",
  },
];

// DB의 faqItems가 유효하면 그것을, 아니면 기본 FAQ를 돌려준다.
export function resolveFaq(items: unknown): FaqItem[] {
  if (
    Array.isArray(items) &&
    items.length > 0 &&
    items.every(
      (it) =>
        it &&
        typeof it === "object" &&
        typeof (it as FaqItem).question === "string" &&
        typeof (it as FaqItem).answer === "string",
    )
  ) {
    return items as FaqItem[];
  }
  return DEFAULT_FAQ;
}

// 홈/소개 페이지의 편집 가능한 문구 기본값. DB의 해당 컬럼이 비어 있으면
// 이 값이 쓰이고, 관리자가 설정에서 값을 채우면 그 값으로 대체된다.
// (긴/여러 줄 기본값을 Prisma @default에 넣으면 이스케이프가 까다로워
//  기본값은 코드에 두고, DB 컬럼은 빈 문자열 기본으로 둔다.)
export const DEFAULT_COPY = {
  // 홈 히어로
  heroTitleLine1: "치악산의 자연을 담아",
  heroTitleLine2: "정성을 전합니다",
  heroSubtitle:
    "강원도 원주 치악산 자락의 맑은 공기와 청정한 땅에서 손수 정성껏 키운 미니 밤호박입니다.",
  heroStat1Value: "치악산",
  heroStat1Label: "청정 고원 재배",
  heroStat2Value: "100%",
  heroStat2Label: "정성껏 키운",
  heroStat3Value: "직송",
  heroStat3Label: "산지 직거래",
  // 홈 섹션 설명
  productsSectionDesc: "치악산 자락에서 정성껏 키운 제철 농산물을 소개합니다.",
  diarySectionDesc: "현들팜의 하루하루를 솔직하게 기록합니다",
  storyText:
    "강원특별자치도 원주시 소초면, 치악산이 품어주는 청정 고원에서 현들팜이 시작되었습니다. 농부가 정성껏 키운 농산물을 산지의 신선함 그대로 식탁에 전해드립니다.",
  // 소개 페이지
  aboutHeroSubtitle:
    "강원도 원주 치악산 자락에서\n자연과 함께 정성껏 키운 농산물을 전합니다.",
  aboutStory:
    "현들팜은 강원도 원주시 소초면, 치악산 자락에 자리한 작은 농장입니다. 해발 400m의 서늘하고 깨끗한 공기 속에서, 일교차가 큰 자연환경을 그대로 살려 미니 밤호박을 재배하고 있습니다.\n\n우리 땅에서 우리가 직접 키운 농산물을 소비자 여러분께 직접 전달하겠다는 마음으로 농장 직거래를 시작했습니다. 중간 유통 없이 수확한 신선함 그대로 보내드립니다.\n\n수확한 농산물은 크기와 상태를 하나하나 살펴 선별하고, 산지의 신선함을 그대로 담아 정성껏 보내드립니다.",
  aboutProductTitle: "미니 밤호박이란?",
  aboutProductIntro:
    "미니 밤호박은 한 손에 쏙 들어오는 작은 크기의 단호박입니다. 밤처럼 포슬포슬한 식감과 진한 단맛이 특징으로, 치악산 자락의 큰 일교차 속에서 자라 당도가 더욱 높습니다.",
} as const;

export type SiteCopy = Record<keyof typeof DEFAULT_COPY, string>;
export type SiteCopyKey = keyof typeof DEFAULT_COPY;

export const SITE_COPY_KEYS = Object.keys(DEFAULT_COPY) as SiteCopyKey[];

// 설정값과 기본값을 합쳐 "실제로 보여줄 문구"를 만든다. 설정값이 비어
// 있으면(미설정) 기본값을 쓴다. 컴포넌트와 관리자 폼 프리필이 공유한다.
export function resolveSiteCopy(
  settings: Partial<SiteSettings> | null | undefined,
): SiteCopy {
  const out = {} as SiteCopy;
  for (const key of SITE_COPY_KEYS) {
    const value = settings?.[key];
    out[key] =
      typeof value === "string" && value.trim() !== ""
        ? value
        : DEFAULT_COPY[key];
  }
  return out;
}
